/**
 * The Australia day-sheet as a plan: the wire shapes the Board renders,
 * with the sheet's amounts (5 × 500 → 2 × 1,000 every 35 min) and the live
 * plan's names/colours. Every number flows through the app's own
 * formatters downstream — nothing is typed into a chapter.
 * For Media Devoted | Created By Ciro Autiero
 */
import type {
  DropGenContainerSummary,
  DropGenPlanDetail,
  DropGenSlotLite,
} from '@trafficpulse/shared';

export const DEMO_TZ = 'Australia/Sydney';
export const DEMO_RUN_DATE = '2026-10-06';

/** The day-fixture's group ids (demo-day.ts) — one list of drops, each naming its group. */
const CLICKERS_ID = 'g-clickers';
const FTDS_ID = 'g-ftds';

const slots = (
  amounts: Array<number | null>,
  groupsOf: (i: number) => string[] = () => [CLICKERS_ID],
): DropGenSlotLite[] =>
  amounts.map((amount, i) => ({ id: `s${i + 1}`, amount, enabled: true, listGroupIds: groupsOf(i), pinnedAtMin: null }));

const container = (
  over: Partial<DropGenContainerSummary> & Pick<DropGenContainerSummary, 'id' | 'name' | 'color'>,
): DropGenContainerSummary => ({
  planId: 'demo-plan',
    kind: 'CONTAINER',
    masterId: null,
    ownSections: {},
    groups: [],
  enabled: true,
  orderIndex: 0,
  campaignWiserId: 8188,
  staticTagId: null,
  startMode: 'FIXED_TIME',
  gapMin: 0,
  startAtMin: null,
  endAtMin: null,
  intervalMin: 30,
  defaultAmount: 500,
  failMode: 'STOP_FOR_DAY',
  slotMode: 'LIST',
  scalingEnabled: false,
  slotCount: 0,
  groupCount: 0,
  poolCount: 2,
  textGroupCount: 1,
  hasSenderGroup: true,
  slots: [],
  ...over,
});

export const WARM_UP = container({
  id: 'demo-warm-up',
  name: 'WARM UP',
  color: '#6366f1',
  orderIndex: 0,
  startAtMin: 11 * 60,
  intervalMin: 35,
  defaultAmount: 500,
  slotCount: 7,
  groupCount: 1,
  slots: slots([500, 500, 500, 500, 500, 1000, 1000]),
});

export const CLEARANCE_CLICKS = container({
  id: 'demo-clearance-clicks',
  name: 'CLEARANCE-CLICKS',
  color: '#f59e0b',
  orderIndex: 1,
  startAtMin: 14 * 60,
  endAtMin: 21 * 60,
  intervalMin: 15,
  defaultAmount: 300,
  slotCount: 29,
  groupCount: 1,
  slots: slots(Array.from({ length: 29 }, () => null)),
});

export const SEGMENTS = container({
  id: 'demo-segments',
  name: 'SEGMENTS',
  color: '#ec4899',
  orderIndex: 2,
  startMode: 'AFTER_PREVIOUS',
  gapMin: 10,
  intervalMin: 60,
  defaultAmount: 800,
  slotCount: 6,
  groupCount: 2,
  // SEGMENTS alternates its two groups down the list, as the day fixture plans it
  slots: slots([null, null, 900, null, null, null], (i) => [i % 2 === 0 ? CLICKERS_ID : FTDS_ID]),
});

export const DEMO_PLAN: DropGenPlanDetail = {
  id: 'demo-plan',
  countryKey: 'australia',
  recipientTimezone: DEMO_TZ,
  masters: [],
  enabled: true,
  killSwitch: false,
  autoGenerate: false,
  generateAtMin: 8 * 60,
  windowStartMin: 9 * 60,
  windowEndMin: 21 * 60,
  maxSendFailures: 3,
  autoSendFailStreak: 0,
  autoSendHaltedAt: null,
  autoSendHaltKind: null,
  autoSendHaltReason: null,
  autoSendHaltRunDate: null,
  containerCount: 3,
  enabledContainerCount: 3,
  planIssues: [],
  createdAt: `${DEMO_RUN_DATE}T00:00:00.000Z`,
  updatedAt: `${DEMO_RUN_DATE}T00:00:00.000Z`,
  containers: [WARM_UP, CLEARANCE_CLICKS, SEGMENTS],
};

/** The gateway pool as the editor letters it (A, B, C) — chapter 4. */
export const DEMO_ROUTES = [
  { routeId: 1751, name: 'IT-Decision' },
  { routeId: 1760, name: 'AU-Direct' },
  { routeId: 1772, name: 'AU-Backup' },
];
export const DEMO_SPLIT_WEIGHTS = [50, 30, 20];
/** Prompt groups ("Folder / Leaf") and the sender group — chapter 4. */
export const DEMO_TEXT_GROUPS = ['Clearance / Short hooks', 'Clearance / Bonus'];
export const DEMO_SENDER_GROUP = 'AU senders';
export const DEMO_OPTIONS = [
  { label: 'Duplicates', value: 'off — skip numbers messaged today' },
  { label: 'Link domain', value: 'auto' },
  { label: 'Batches', value: '500 every 2 min — or AI pacing, never both' },
];
